import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useLogin} from '../../hooks/useApi';
import {useAuth} from '../../hooks/useAuth';
import {useAppDispatch} from '../../hooks/redux';
import {setCurrentPatient} from '../../slices/patientSlice';
import {ThemedLogo} from '../themed/ThemedLogo';
import ScrollingPeople from './ScrollingPeople';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const loginMutation = useLogin();
    const {login} = useAuth();
    const dispatch = useAppDispatch();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const response = await loginMutation.mutateAsync({username, password});

            // Set current patient from login response (like old implementation)
            if (response?.userInfo) {
                dispatch(
                    setCurrentPatient({
                        id: response.userInfo.id,
                        firstName: response.userInfo.firstName,
                        lastName: response.userInfo.lastName,
                        fullName: `${response.userInfo.firstName} ${response.userInfo.lastName}`,
                        birthdate: response.userInfo.birthday || '',
                    })
                );
            }

            // Set auth user - only called if mutation was successful
            login(response);
        } catch (error) {
            // Error is handled by React Query and displayed via loginMutation.error
            // Do not call login() - user should remain unauthenticated
            console.error('Login failed:', error);
        }
    };

    return (
        <div className="h-screen flex bg-mld-background overflow-hidden">
            {/* Left side - Login Form */}
            <div className="flex-1 flex items-center justify-center bg-mld-background px-4 sm:px-6 lg:px-20">
                <div className="max-w-sm w-full space-y-8">
                    {/* Logo */}
                    <div className="text-center">
                        <ThemedLogo className="mx-auto h-6 mb-12" />
                    </div>

                    {/* Title and Subtitle */}
                    <div className="text-center">
                        <h2 className="text-3xl font-light text-mld-menu-text mb-2">Hello</h2>
                        <p className="text-mld-menu-text text-base">Please sign in to continue</p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                        <div className="space-y-4">
                            <div>
                                <input
                                    id="username"
                                    type="text"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors text-gray-700 placeholder-gray-500 bg-white"
                                    placeholder="Email"
                                />
                            </div>

                            <div>
                                <input
                                    id="password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors text-gray-700 placeholder-gray-500 bg-white"
                                    placeholder="Password"
                                />
                            </div>
                        </div>

                        {/* Login Button */}
                        <button
                            type="submit"
                            disabled={loginMutation.isPending}
                            className="w-full bg-mld-login-button hover:bg-opacity-90 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {loginMutation.isPending ? 'Signing In...' : 'Login'}
                        </button>

                        {/* Error Message */}
                        {loginMutation.error && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-600 text-sm text-center">
                                    {loginMutation.error.message === 'User not verified'
                                        ? 'Please verify your email address before logging in. Check your inbox for a verification email.'
                                        : loginMutation.error.message || 'Login failed. Please try again.'}
                                </p>
                            </div>
                        )}

                        {/* Forgot Password Link with divider below */}
                        <div className="text-center space-y-3">
                            <Link
                                to="/reset"
                                className="text-mld-muted-text hover:text-mld-login-button text-sm transition-colors">
                                Forgot your password?
                            </Link>
                            <div className="mx-auto w-16 h-0.5 bg-mld-login-button mt-2"></div>
                        </div>

                        {/* Sign Up Link */}
                        <div className="text-center">
                            <span className="text-mld-menu-text text-sm font-bold">Not a member yet? </span>
                            <Link
                                to="/register"
                                className="text-mld-muted-text hover:text-mld-login-button text-sm font-normal transition-colors">
                                Sign Up Here
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right side - Scrolling People */}
            <div className="hidden lg:flex flex-1 bg-mld-background">
                <ScrollingPeople />
            </div>
        </div>
    );
};

export default LoginPage;
